import React from 'react';
import Navbar from './global-components/navbar-v2';
import PageHeader from './global-components/page-header';
import LegalContent from './section-components/legal-content';
import Footer from './global-components/footer-v2';

const PrivacyPolicy = () => {
	const content = {
		kicker: 'Converzex — A DotZero Tech LLC Product',
		meta: 'Effective Date: August 14, 2026 | Last Updated: August 14, 2026',
		sections: [
			{
				id: 'who-we-are',
				title: '1. Who We Are',
				paragraphs: [
					'Converzex is an AI-powered customer engagement and automation platform operated by DotZero Tech LLC. It helps businesses communicate with customers, capture and qualify leads, automate follow-ups, schedule appointments, and connect conversations with business systems.',
					'We may collect and use personal information when you visit our website, create or use a Converzex account, contact us, subscribe to our Services, or interact with a Converzex-powered experience.'
				]
			},
			{
				id: 'contact-us',
				title: '2. Contact Us',
				paragraphs: ['For privacy questions or requests, contact:'],
				list: [
					<>Email: <a href="mailto:legal@converzex.com">legal@converzex.com</a></>,
					<>Website: <a href="https://www.converzex.com" target="_blank" rel="noopener noreferrer">www.converzex.com</a></>,
					'Legal entity: DotZero Tech LLC',
					'Address: 5900 Balcones Drive STE 100 Austin, TX 78731,USA'
				]
			},
			{
				id: 'how-we-use',
				title: '3. How We Use Personal Information',
				paragraphs: ['We may use personal information to:'],
				list: [
					'Create, secure, and manage accounts.',
					'Provide dashboards, conversations, leads, call logs, integrations, packages, invoices, billing, and other Services.',
					'Provide customer support and resolve service issues.',
					'Maintain platform security, reliability, and performance.',
					'Process payments and subscriptions.',
					'Respond to enquiries and business requests.',
					'Send product updates, educational content, offers, and other marketing where permitted.',
					'Process conversations and lead information when a business uses Converzex to communicate with its customers.',
					'Operate, analyze, secure, and improve our website and Services.'
				]
			},
			{
				id: 'ai-training',
				title: '4. AI Training and Improvement',
				paragraphs: [
					'Converzex may use conversational data from customer accounts and content from customer public websites to train, test, and improve the AI models and features used by Converzex.',
					'Business customers are responsible for informing their own users through their privacy notices where required. Customers who want to restrict this use can contact us to discuss available account settings or a separate written agreement.'
				]
			},
			{
				id: 'information-we-collect',
				title: '5. Information We May Collect',
				list: [
					'Name, email address, phone number, company name, and account details.',
					'Login information, workspace settings, roles, and permissions.',
					'Subscription, invoice, payment-status, and transaction information.',
					'Chat messages, questions, responses, lead information, preferences, notes, tags, summaries, and workflow results.',
					'Phone numbers, call dates and times, duration, status, transcripts, summaries, and call recordings.',
					'Lead information such as location, budget, product interest, service requirements, and appointment details.',
					'Information exchanged with connected services such as websites, Shopify, HubSpot, CRM systems, social platforms, and messaging providers.',
					'Technical information such as IP address, browser, device, operating system, logs, and diagnostics.',
					'Cookie and analytics information.',
					'Website content used for AI training as described above.'
				]
			},
			{
				id: 'call-recording',
				title: '6. Call Recording and AI',
				paragraphs: [
					'Voice calls made or received through Converzex are recorded by default. Customers are responsible for providing any required notice and obtaining any required consent before or during a recorded call.',
					'Converzex uses AI to understand conversations, generate responses, qualify leads, summarize calls, identify intent, route enquiries, schedule appointments, and automate customer-engagement workflows.',
					'AI-generated results may not always be accurate. Customers are responsible for setting appropriate workflows, review processes, and human escalation where needed.'
				]
			},
			{
				id: 'business-responsibilities',
				title: '7. Business Customer Responsibilities',
				paragraphs: [
					'When a business uses Converzex to process information about its customers, leads, patients, shoppers, visitors, or other individuals, that business is responsible for deciding why and how the information is processed and for providing required privacy notices and consents.',
					'Customers are also responsible for configuring Chat Agents, voice workflows, integrations, retention settings, and communications in accordance with applicable laws.'
				]
			},
			{
				id: 'data-retention',
				title: '8. Data Retention',
				paragraphs: [
					'We keep personal information, including call recordings and transcripts, for as long as reasonably needed to provide the Services, maintain records, meet legal requirements, resolve disputes, prevent fraud, and protect our business interests. Retention periods may vary by data type, account settings, agreements, and legal requirements.'
				]
			},
			{
				id: 'service-providers',
				title: '9. Service Providers',
				paragraphs: [
					'We may share information with service providers that help us operate Converzex, including cloud and infrastructure providers, AI and machine-learning providers, voice and communication providers, payment providers, analytics providers, security providers, integration providers, and professional advisers.',
					'We may also disclose information when required or permitted by law.'
				]
			},
			{
				id: 'international-transfers',
				title: '10. International Transfers',
				paragraphs: [
					'Converzex and its service providers may process information in countries other than the country where it was collected. Where required, we use appropriate safeguards for international data transfers.'
				]
			},
			{
				id: 'privacy-rights',
				title: '11. Your Privacy Rights',
				paragraphs: [
					'Depending on applicable law, you may have rights to access, correct, delete, restrict, or transfer your personal information, object to certain processing or marketing, withdraw consent, and raise a complaint with a relevant data-protection authority.',
					<>Contact <a href="mailto:legal@converzex.com">legal@converzex.com</a> to make a privacy request. We may ask you to verify your identity.</>
				]
			},
			{
				id: 'security',
				title: '12. Security',
				paragraphs: [
					'We use reasonable technical and organizational measures to protect personal information from unauthorized access, loss, misuse, alteration, or disclosure. No internet service can guarantee complete security.'
				]
			},
			{
				id: 'cookies',
				title: '13. Cookies',
				paragraphs: [
					'We use cookies and similar technologies for essential functions, preferences, analytics, performance, and, where applicable, marketing. Where required by law, we provide a consent option for non-essential cookies.'
				]
			},
			{
				id: 'dpa',
				title: '14. Data Processing Agreement',
				paragraphs: [
					<>Business customers that require a Data Processing Agreement may request one by contacting <a href="mailto:legal@converzex.com">legal@converzex.com</a></>
				]
			},
			{
				id: 'children',
				title: '15. Children\'s Privacy',
				paragraphs: [
					'Converzex is designed for businesses and is not directed to children under 13. We do not knowingly collect personal information directly from children under 13.'
				]
			},
			{
				id: 'changes',
				title: '16. Changes to This Policy',
				paragraphs: [
					'We may update this Privacy Policy as our Services, technology, business practices, or legal requirements change. The latest version will be posted on the website with an updated date.'
				]
			},
			{
				id: 'contact',
				title: '17. Contact',
				paragraphs: [
					<>For privacy questions, requests, or complaints, contact <a href="mailto:legal@converzex.com">legal@converzex.com</a> or write to DotZero Tech LLC, 5900 Balcones Drive STE 100 Austin, TX 78731,USA.</>
				]
			}
		]
	};

	return (
		<div>
			<Navbar />
			<PageHeader headertitle="Privacy Policy" />
			<LegalContent {...content} />
			<Footer />
		</div>
	);
};

export default PrivacyPolicy;
